import { brl, pct, qty } from '../lib/format.ts';

/** Budget × actual of one line, as computed by the API (@ana/domain budgetVariance). */
export interface BudgetVariance {
  budget: number | null;
  actual: number;
  varianceAbs: number | null;
  variancePct: number | null;
  outcome: 'NO_BUDGET' | 'ON_BUDGET' | 'FAVORABLE' | 'UNFAVORABLE';
}

export interface DreBudget {
  homologated: boolean;
  decision: string;
  month: string;
  actualThrough: string;
  partial: boolean;
  complete: boolean;
  grossRevenue: BudgetVariance;
  deductions: BudgetVariance;
  netRevenue: BudgetVariance;
  costs: BudgetVariance;
  expenses: BudgetVariance;
  operatingResult: BudgetVariance;
  budgetMargin: number | null;
  marginPointsChange: number | null;
  parametrizedDeductionsCents: number;
  accounts: ({ dreAccountId: string; code: string; name: string; group: string } & BudgetVariance)[];
}

const OUTCOME: Record<BudgetVariance['outcome'], { label: string; tone: string }> = {
  FAVORABLE: { label: 'Favorável', tone: 'ok' },
  UNFAVORABLE: { label: 'Desfavorável', tone: 'danger' },
  ON_BUDGET: { label: 'No orçado', tone: 'info' },
  NO_BUDGET: { label: 'Sem orçamento', tone: 'muted' },
};

export function OutcomeBadge({ outcome }: { outcome: BudgetVariance['outcome'] }) {
  const o = OUTCOME[outcome];
  return <span className={`badge ${o.tone}`}>{o.label}</span>;
}

/** Variation with an explicit sign: "+R$ 50,00", "−10". */
export function signed(v: number | null, kind: 'money' | 'qty' = 'money'): string {
  if (v === null) return '—';
  const abs = kind === 'money' ? brl(Math.abs(v)) : qty(Math.abs(v));
  return v > 0 ? `+${abs}` : v < 0 ? `−${abs}` : abs;
}

export function signedPct(v: number | null): string {
  if (v === null) return 'N/D';
  return v > 0 ? `+${pct(v)}` : pct(v).replace('-', '−');
}

/** Table cells Orçado · Realizado · Variação · Variação % · Situação for one line. */
export function VarianceCells({ v, kind = 'money' }: { v: BudgetVariance; kind?: 'money' | 'qty' }) {
  const fmt = kind === 'money' ? brl : qty;
  return (
    <>
      <td className="num">{fmt(v.budget)}</td>
      <td className="num">{fmt(v.actual)}</td>
      <td className="num">{signed(v.varianceAbs, kind)}</td>
      <td className="num">{signedPct(v.variancePct)}</td>
      <td>
        <OutcomeBadge outcome={v.outcome} />
      </td>
    </>
  );
}

export const BUDGET_RULE_TITLE =
  'Orçamento com regra provisória: a aprovação do orçamento e a revisão de meses encerrados aguardam a decisão do Financeiro e da Gestão (D-24).';

export const BUDGET_GROUP_LABEL: Record<string, string> = {
  RECEITA: 'Receita',
  FLUXO: 'Fluxo',
  DEDUCAO: 'Dedução',
  CUSTO: 'Custo',
  DESPESA: 'Despesa',
};
